import { ENotificationOrderStateCentroid } from '@webtrader/core/api/notifications';
import { ENotificationTemplate } from '@webtrader/core/api/notifications/constants';
import i18n from '@webtrader/core/i18n';
import { IColors } from '@webtrader/core/themes/colors';
import { rgba } from '@webtrader/core/themes/utils';

export const mapNotificationOrderStateToNotificationTitle = (state: ENotificationOrderStateCentroid) => {
  const CENTROID_NOTIFICATION_ITEM_TITLE_MAP: Record<ENotificationOrderStateCentroid, string> = {
    [ENotificationOrderStateCentroid.NEW]: i18n.t('Order Pending'),
    [ENotificationOrderStateCentroid.PARTIALLY_FILLED]: i18n.t('Order Partially Filled'),
    [ENotificationOrderStateCentroid.FILLED]: i18n.t('Order Filled'),
    [ENotificationOrderStateCentroid.REJECTED]: i18n.t('Order Rejected'),
    [ENotificationOrderStateCentroid.CANCELED]: i18n.t('Order Deleted'),
    [ENotificationOrderStateCentroid.CLOSED]: i18n.t('Position Closed'),
    [ENotificationOrderStateCentroid.MODIFIED]: i18n.t('Position Modified')
  };
  return state && state in CENTROID_NOTIFICATION_ITEM_TITLE_MAP
    ? CENTROID_NOTIFICATION_ITEM_TITLE_MAP[state]
    : i18n.t('Order Created');
};

export const mapNotificationOrderStateToDateLabel = (state: ENotificationOrderStateCentroid | undefined) => {
  const CENTROID_ORDER_STATE_DATE_LABEL_MAP: Record<ENotificationOrderStateCentroid, string> = {
    [ENotificationOrderStateCentroid.NEW]: i18n.t('Created Date'),
    [ENotificationOrderStateCentroid.PARTIALLY_FILLED]: i18n.t('Filled Date'),
    [ENotificationOrderStateCentroid.FILLED]: i18n.t('Filled Date'),
    [ENotificationOrderStateCentroid.REJECTED]: i18n.t('Rejected Date'),
    [ENotificationOrderStateCentroid.CANCELED]: i18n.t('Cancelled Date'),
    [ENotificationOrderStateCentroid.CLOSED]: i18n.t('Closed Date'),
    [ENotificationOrderStateCentroid.MODIFIED]: i18n.t('Modified Date')
  };
  return state && state in CENTROID_ORDER_STATE_DATE_LABEL_MAP
    ? CENTROID_ORDER_STATE_DATE_LABEL_MAP[state]
    : i18n.t('Created Date');
};

export const mapNotificationOrderStateToAlertTitle = (state: ENotificationOrderStateCentroid) => {
  const CENTROID_ORDER_STATE_ALERT_TITLE_MAP: Record<ENotificationOrderStateCentroid, string> = {
    [ENotificationOrderStateCentroid.NEW]: i18n.t('Order has been submitted'),
    [ENotificationOrderStateCentroid.PARTIALLY_FILLED]: i18n.t('Order has been partially filled'),
    [ENotificationOrderStateCentroid.FILLED]: i18n.t('Order has been filled'),
    [ENotificationOrderStateCentroid.REJECTED]: i18n.t('Order has been rejected'),
    [ENotificationOrderStateCentroid.CANCELED]: i18n.t('Order has been deleted'),
    [ENotificationOrderStateCentroid.CLOSED]: i18n.t('Position has been closed'),
    [ENotificationOrderStateCentroid.MODIFIED]: i18n.t('Position has been modified')
  };
  return state && state in CENTROID_ORDER_STATE_ALERT_TITLE_MAP
    ? CENTROID_ORDER_STATE_ALERT_TITLE_MAP[state]
    : i18n.t('Order has been created');
};

const CENTROID_ORDER_NOTIFICATION_TEMPLATES = [
  ENotificationTemplate.NT_CTD_PNDG_ORD_SUBMITTED,
  ENotificationTemplate.NT_CTD_ORD_EXECUTED,
  ENotificationTemplate.NT_CTD_ORD_REJECTED,
  ENotificationTemplate.NT_CTD_PNDG_ORD_DELETED,
  ENotificationTemplate.NT_CTD_ORD_TPSL_EXECUTED
] as Partial<ENotificationTemplate>[];

export const checkIsCentroidOrder = (notificationTemplate: ENotificationTemplate) => {
  return CENTROID_ORDER_NOTIFICATION_TEMPLATES.includes(notificationTemplate);
};

export const mapNotificationTemplateToOrderIdText = (template: ENotificationTemplate) => {
  switch (template) {
    case ENotificationTemplate.NT_CTD_ORD_TPSL_EXECUTED:
      return i18n.t('Position ID');
    default:
      return i18n.t('Order ID');
  }
};

export const mapNotificationOrderStateToColor = (orderState: ENotificationOrderStateCentroid, themeColors: IColors) => {
  switch (orderState) {
    case ENotificationOrderStateCentroid.REJECTED:
      return {
        backgroundColor: rgba(themeColors.orderStatusTag.violet, 0.2),
        textColor: themeColors.orderStatusTag.violet
      };
    case ENotificationOrderStateCentroid.NEW:
      return {
        backgroundColor: rgba(themeColors.orderStatusTag.amber, 0.2),
        textColor: themeColors.orderStatusTag.amber
      };
    case ENotificationOrderStateCentroid.CANCELED:
      return { backgroundColor: themeColors.background[2], textColor: themeColors.text[1] };
    default:
      return {
        backgroundColor: rgba(themeColors.orderStatusTag.cyan, 0.2),
        textColor: themeColors.orderStatusTag.cyan
      };
  }
};
